import type { Assignment, AssignmentEmployee, ProjectSummary } from "@/hooks/useProjectBoardData";
import type { Database } from "@/types/database.types";
import { currentWeekStartISO } from "@/utils/dateUtils";
import { formatEmployeeDisplayName } from "@/utils/nameUtils";
import { resizeWeeklyHoursAllocation } from "@/utils/projectUtils";

export interface ProjectModalFormData {
  name: string;
  /**
   * Stored custom color (`#rrggbb`), or null when the project uses the default
   * id-derived color. Kept nullable (not the effective display color) so saving
   * an unrelated edit doesn't silently pin the auto color, and a duplicate of an
   * uncustomized project derives its own color from the new id.
   */
  color: string | null;
  office_id: string;
  client: string;
  client_id: string | null;
  start_date: string;
  num_weeks: number;
  budget: string;
  description: string;
  potential_future_work: string;
  success_ingredients: string;
  tech_stack: string[];
  engagement_type: string[];
  deal_stage: string;
  likelihood_to_close: string;
  category_id: string;
}

export interface DuplicatedAssignmentDraft {
  sourceAssignmentId: string;
  insert: Database["public"]["Tables"]["project_assignments"]["Insert"] & { id: string };
  employees: AssignmentEmployee[];
}

export function getCurrentWeekStartDate(now = new Date(Date.now())): string {
  // The browser-local week, matching the date pickers' "this week" floor.
  return currentWeekStartISO(now);
}

export function getProjectModalFormData(project: ProjectSummary): ProjectModalFormData {
  return {
    name: project.name,
    color: project.color ?? null,
    office_id: project.office_id,
    client: project.client ?? "",
    client_id: project.client_id ?? null,
    start_date: project.start_date,
    num_weeks: project.num_weeks ?? 8,
    budget: project.budget?.toString() ?? "",
    description: project.description ?? "",
    potential_future_work: project.potential_future_work ?? "",
    success_ingredients: project.team_success_ingredients ?? "",
    tech_stack: project.tech_stack ?? [],
    engagement_type: project.engagement_type ?? [],
    deal_stage: project.deal_stage?.name ?? "Budget & Plan",
    likelihood_to_close: String(project.likelihood_to_close),
    category_id: project.category_id,
  };
}

export function getDuplicatedProjectModalFormData(
  project: ProjectSummary,
  now = new Date(Date.now()),
): ProjectModalFormData {
  return {
    ...getProjectModalFormData(project),
    name: `Duplicate of ${project.name}`,
    start_date: getCurrentWeekStartDate(now),
    num_weeks: project.num_weeks ?? 8,
  };
}

export function buildDuplicatedAssignmentDrafts(
  assignments: Assignment[],
  newProjectId: string,
  newNumWeeks: number,
  keepAllAssignments: boolean,
  createId: () => string = () => crypto.randomUUID(),
): DuplicatedAssignmentDraft[] {
  return assignments.map((assignment, index) => {
    const id = createId();
    return {
      sourceAssignmentId: assignment.id,
      insert: {
        id,
        project_id: newProjectId,
        required_role_id: assignment.required_role_id,
        weekly_hours_allocated: resizeWeeklyHoursAllocation(
          assignment.weekly_hours_allocated ?? [],
          newNumWeeks,
        ),
        is_confirmed: keepAllAssignments ? assignment.is_confirmed : false,
        notes: assignment.notes,
        display_order: assignment.display_order ?? index,
      },
      employees: keepAllAssignments ? [...(assignment.employees ?? [])] : [],
    };
  });
}

export function buildDuplicatedAssignmentEmployeeRows(
  drafts: DuplicatedAssignmentDraft[],
): Database["public"]["Tables"]["assignment_employees"]["Insert"][] {
  return drafts.flatMap((draft) =>
    draft.employees.map((employee) => ({
      assignment_id: draft.insert.id,
      employee_id: employee.id,
    })),
  );
}

export function formatDuplicatedAssignmentEmployeeAuditNames(
  employees: AssignmentEmployee[],
): string[] {
  return employees.map((employee) => formatEmployeeDisplayName(employee) || employee.id);
}
